import useScreenBreakpoint from "../../../hooks/useScreenBreakpoint";
import * as moment from "moment";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { getElasticSearchURLS } from "../../../selectors/enviromentSelectors";
import { useUserInfoState } from "../../../selectors/userInfoSelectors";
import PersesSearchAPI from "../../../services/persesSearchService";
import activitiesTypes from "../../../utils/constants/activities/activitiesTypes";
import { QUERY_STATUS } from "../../../utils/constants/dashboard/dashboardModuleConstants";
import { CalendarActivityTypes } from "@customTypes/filter";
import { DoneActivitiesByMonthResponse } from "@customTypes/services/responses";

type ParsedDoneActivities = {
  months: string[];
  series: {
    name: string;
    data: number[];
  }[];
  noData?: boolean;
};

const parseDoneActivitiesByMonthResponse = (
  response: DoneActivitiesByMonthResponse,
  monthRange: number,
  calendarActivityTypes: CalendarActivityTypes[],
  isMobileScreen: boolean
): ParsedDoneActivities => {
  const noData = response.length === 0;

  const lastMonthsStartDates = Array.from({ length: monthRange }, (_, index) =>
    moment()
      .subtract(monthRange - index - 1, "months")
      .startOf("month")
      .format("YYYY-MM-DD")
  );

  const series = calendarActivityTypes.map((activityType: keyof typeof activitiesTypes) => {
    const data = lastMonthsStartDates.map((monthStartDate) => {
      const activityBucketInMonth = response.find(
        (bucket) => bucket.firstKey === monthStartDate && bucket.secondKey === activityType
      );
      return activityBucketInMonth ? activityBucketInMonth.count : 0;
    });
    return { name: activitiesTypes[activityType].pluralLabel, data };
  });

  const months = lastMonthsStartDates.map((monthStartDate) => {
    const monthName = moment(monthStartDate, "YYYY-MM-DD").format(isMobileScreen ? "MMM" : "MMMM");
    const year = moment(monthStartDate, "YYYY-MM-DD").format("YYYY");
    const formattedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);

    const monthIsJanuary = ["Janeiro", "Jan"].includes(formattedMonth);

    if (monthIsJanuary) return `${formattedMonth}/${year}`;
    return formattedMonth;
  });

  return { months, series, noData };
};

const useDoneActivitiesByMonthChartQuery = (
  monthLengthByPowers: number,
  calendarActivityTypes: CalendarActivityTypes[]
): [string, ParsedDoneActivities] => {
  const [doneActivities, setDoneActivities] = useState<ParsedDoneActivities>({
    months: [],
    series: [],
  });
  const [status, setStatus] = useState(QUERY_STATUS.loading);
  const userInfo = useUserInfoState();

  const userId = userInfo.id;

  const { isMobileScreen } = useScreenBreakpoint();

  const END_OF_THIS_MONTH = moment().endOf("month").format("YYYY-MM-DD");
  const START_OF_MONTH_RANGE = moment()
    .subtract(monthLengthByPowers - 1, "months")
    .startOf("month")
    .format("YYYY-MM-DD");

  const PERSES_CODED_URLS = useSelector(getElasticSearchURLS);
  const perses = new PersesSearchAPI(PERSES_CODED_URLS);

  const { data, isSuccess, isLoading, isFetching } = useQuery(
    ["doneActivitiesByMonth", { from: START_OF_MONTH_RANGE, to: END_OF_THIS_MONTH }],
    () =>
      perses
        .getDoneActivitiesByMonth(userId, START_OF_MONTH_RANGE, END_OF_THIS_MONTH)
        .then(({ buckets }) => buckets),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (isLoading) return;
    if (isFetching) {
      setStatus(QUERY_STATUS.refeching);
      return;
    }
    if (isSuccess) {
      const parsedData = parseDoneActivitiesByMonthResponse(
        data,
        monthLengthByPowers,
        calendarActivityTypes,
        isMobileScreen
      );
      setDoneActivities(parsedData);
      setStatus(QUERY_STATUS.success);
    }
  }, [
    data,
    isSuccess,
    monthLengthByPowers,
    isLoading,
    isFetching,
    calendarActivityTypes,
    isMobileScreen,
  ]);

  return [status, doneActivities];
};

export default useDoneActivitiesByMonthChartQuery;
